import { Scissors } from '@app/components/icons/scissors';
import { useTranslation } from '@hopin-team/provider-translation';
import React from 'react';

import { type TimelineZoomScale } from './edit-view-player-context';
import { ZoomSlider } from './zoom-slider';

/**
 * Renders a hint string with keyboard key tokens wrapped in <kbd> badges.
 * Tokens are delimited by backticks, e.g. "`S` to split" renders S as a key badge.
 */
const HintText = ({ text }: { text: string }): React.JSX.Element => {
  const parts = text.split(/(`[^`]+`)/g);
  return (
    <span className="flex items-center gap-1 flex-wrap">
      {parts.map((part, index) => {
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <kbd
              key={index}
              className="inline-flex items-center rounded bg-spring-neutral-b0/20 px-1.5 py-0.5 text-xs leading-none text-spring-neutral-b0"
            >
              {part.slice(1, -1)}
            </kbd>
          );
        }
        return (
          <span key={index} className="text-sm text-spring-neutral-b0/60">
            {part}
          </span>
        );
      })}
    </span>
  );
};

type SegmentVideoPlayerControlsProps = {
  zoomScale: TimelineZoomScale;
  onZoomScaleChange?: (nextScale: TimelineZoomScale) => void;
  onSplitAtPlayhead?: () => void;
  canSplitAtPlayhead?: boolean;
  hints?: string[];
};

export const SegmentVideoPlayerControls = ({
  zoomScale,
  onZoomScaleChange,
  onSplitAtPlayhead,
  canSplitAtPlayhead = true,
  hints = [],
}: SegmentVideoPlayerControlsProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between gap-3 py-2">
      {hints.length > 0 ? (
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {hints.map((h, i) => (
            <React.Fragment key={h}>
              {i > 0 && (
                <span className="text-spring-neutral-b0/30 select-none">·</span>
              )}
              <HintText text={h} />
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div />
      )}

      <div className="flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={onSplitAtPlayhead}
          disabled={!canSplitAtPlayhead}
          className="rounded-xl w-8 h-8 flex items-center justify-center border transition-colors disabled:opacity-30 disabled:cursor-not-allowed border-spring-neutral-b0 text-spring-neutral-b0 enabled:hover:border-spring-primary enabled:hover:text-spring-primary"
          aria-label={t('content-recording.editor-v2.split-at-playhead')}
        >
          <Scissors className="w-4 h-4" />
        </button>

        <ZoomSlider
          value={zoomScale}
          onChange={onZoomScaleChange}
          ariaLabel={t('content-recording.editor-v2.zoom-scale')}
        />
      </div>
    </div>
  );
};
